import { Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@bloomcode/error-handling';
import { LoadingSpinner, Toaster } from '@bloomcode/ui';
import { useWorkspaceEmbedded } from '@bloomcode/shared-modules';
import { getAppUrl } from '@bloomcode/config';
import { getAuthPortalLoginUrl, getAuthPortalSignupUrl, authPortalUrl } from './config/env';

// Layouts
import MainLayout from './layouts/MainLayout';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';

// Communications Pages
import CommunicationsPage from './pages/communications/CommunicationsPage';

// Preferences Pages
import NotificationPreferencesPage from './pages/preferences/NotificationPreferencesPage';

// Deals Pages
import MyDealsPage from './pages/deals/MyDealsPage';
import DealDetailPage from './pages/deals/DealDetailPage';

// Activities Page (full implementation)
import ActivityTimelinePage from './pages/activities/ActivityTimelinePage';

// Documents Page (full implementation)
import DocumentsPage from './pages/documents/DocumentsPage';

// Forms Pages (full implementation)
import FormsListPage from './pages/forms/FormsListPage';
import FormSubmitPage from './pages/forms/FormSubmitPage';
import MySubmissionsPage from './pages/forms/MySubmissionsPage';

// Meeting Request Page (full implementation)
import RequestMeetingPage from './pages/meetings/RequestMeetingPage';

/**
 * Redirects to portal-account for profile/account management
 */
function AccountPortalRedirect() {
  useEffect(() => {
    const accountPortalUrl = import.meta.env.VITE_ACCOUNT_PORTAL_URL || getAppUrl('portal-account');
    window.location.href = `${accountPortalUrl}/profile`;
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting to account settings...</p>
      </div>
    </div>
  );
}

/**
 * Redirects to portal-support for ticket management
 */
function SupportPortalRedirect() {
  useEffect(() => {
    const supportPortalUrl = import.meta.env.VITE_SUPPORT_PORTAL_URL || getAppUrl('portal-support');
    window.location.href = `${supportPortalUrl}/tickets`;
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting to support portal...</p>
      </div>
    </div>
  );
}

/**
 * Redirects to auth portal for login
 */
function AuthPortalRedirect() {
  useEffect(() => {
    const returnUrl = window.location.origin + '/dashboard';
    window.location.href = getAuthPortalLoginUrl(returnUrl);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting to login...</p>
      </div>
    </div>
  );
}

/**
 * Redirects to auth portal for signup
 */
function AuthPortalSignupRedirect() {
  useEffect(() => {
    const returnUrl = window.location.origin + '/dashboard';
    window.location.href = getAuthPortalSignupUrl(returnUrl, 'Bloomcode CRM');
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting to signup...</p>
      </div>
    </div>
  );
}

/**
 * Redirects to auth portal with token and returnUrl preserved.
 */
function AuthPortalTokenRedirect({ portalPath }: { portalPath: string }) {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = window.location.origin + '/dashboard';
    const url = new URL(portalPath, authPortalUrl);
    searchParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
    if (!url.searchParams.has('returnUrl')) {
      url.searchParams.set('returnUrl', returnUrl);
    }
    window.location.href = url.toString();
  }, [portalPath]);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
}

export function App() {
  const { isEmbedded } = useWorkspaceEmbedded();

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner className="h-screen" />}>
        <Routes>
          {/* Public routes */}
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />
          </Route>

          {/* Auth routes - redirect to auth portal */}
          <Route path="/auth/login" element={<AuthPortalRedirect />} />
          <Route path="/auth/register" element={<AuthPortalSignupRedirect />} />
          <Route path="/login" element={<AuthPortalRedirect />} />

          {/* Accept invitation route - redirect to auth portal */}
          <Route
            path="/auth/accept-invitation"
            element={<AuthPortalTokenRedirect portalPath="/accept-invitation" />}
          />

          {/* Protected routes */}
          <Route path="/dashboard" element={<MainLayout requireAuth />}>
            <Route index element={<DashboardPage />} />
          </Route>

          {/* Profile route - redirect to portal-account */}
          <Route path="/profile" element={<AccountPortalRedirect />} />

          {/* Support tickets - redirect to portal-support */}
          <Route path="/tickets/*" element={<SupportPortalRedirect />} />

          {/* Communications route */}
          <Route path="/communications" element={<MainLayout requireAuth />}>
            <Route index element={<CommunicationsPage />} />
          </Route>

          {/* Notification Preferences route */}
          <Route path="/preferences/notifications" element={<MainLayout requireAuth />}>
            <Route index element={<NotificationPreferencesPage />} />
          </Route>

          {/* Deals routes */}
          <Route path="/deals" element={<MainLayout requireAuth />}>
            <Route index element={<MyDealsPage />} />
            <Route path=":id" element={<DealDetailPage />} />
          </Route>

          {/* Activities route */}
          <Route path="/activities" element={<MainLayout requireAuth />}>
            <Route index element={<ActivityTimelinePage />} />
          </Route>

          {/* Documents route */}
          <Route path="/documents" element={<MainLayout requireAuth />}>
            <Route index element={<DocumentsPage />} />
          </Route>

          {/* Forms routes */}
          <Route path="/forms" element={<MainLayout requireAuth />}>
            <Route index element={<FormsListPage />} />
            <Route path="submissions" element={<MySubmissionsPage />} />
            <Route path=":id" element={<FormSubmitPage />} />
          </Route>

          {/* Meeting Request route */}
          <Route path="/meetings/request" element={<MainLayout requireAuth />}>
            <Route index element={<RequestMeetingPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {/* Hide Toaster when embedded — workspace host provides its own */}
      {!isEmbedded && <Toaster />}
    </ErrorBoundary>
  );
}
